import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormat';
import PhoneNumberInput from './PhoneNumberInput';

// Account Management > Affiliates: the accountants and advisers an account manager knows, added as affiliates. No approval and no terms to
// choose (the program's are used); the affiliate is emailed a login at once and the account manager sees who they added.

const errorText = (e, fallback) => (typeof e.response?.data === 'string' && e.response.data ? e.response.data : fallback);
const blank = { name: '', contactPerson: '', email: '', phone: '' };

function AccountManagerAffiliates() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get('/api/account-manager/affiliates')
      .then((r) => setRows(r.data))
      .catch((e) => { setRows([]); toast.error(errorText(e, 'Could not load your affiliates')); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post('/api/account-manager/affiliates', form);
      toast.success(data?.message || 'Affiliate added');
      setForm(blank);
      load();
    } catch (err) {
      toast.error(errorText(err, 'Could not add the affiliate'));
    }
    setBusy(false);
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch (err) { toast.error('Could not copy'); }
  };

  const set = (name) => (e) => setForm({ ...form, [name]: e.target.value });

  return (
    <div data-testid="am-affiliates">
      <p className="text-muted">
        An affiliate is an accountant or adviser who refers businesses to iBigDo and earns a share of what they pay. It does not reduce your
        commission. Add the ones you know: they are emailed a login at once, and a business that signs up with their link or code is credited to them.
      </p>
      <Card className="mb-4">
        <Card.Header><strong>Add an affiliate</strong></Card.Header>
        <Card.Body>
          <Form onSubmit={add}>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Firm or person</Form.Label>
                <Form.Control value={form.name} onChange={set('name')} required placeholder="e.g. Sharma &amp; Associates" data-testid="am-affiliate-name" /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Contact person (optional)</Form.Label>
                <Form.Control value={form.contactPerson} onChange={set('contactPerson')} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Email</Form.Label>
                <Form.Control type="email" value={form.email} onChange={set('email')} required data-testid="am-affiliate-email" />
                <Form.Text className="text-muted">Their login. The invitation goes here and says you introduced them.</Form.Text></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Phone (optional)</Form.Label>
                <PhoneNumberInput name="phone" value={form.phone} onChange={set('phone')} /></Form.Group></Col>
            </Row>
            <Button type="submit" disabled={busy} data-testid="am-affiliate-add">Add and invite</Button>
            <span className="small text-muted ms-3">Up to 10 a day. The standard terms apply; you can&apos;t change them.</span>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><strong>Affiliates you added</strong></Card.Header>
        <Card.Body className="p-0">
          {rows === null ? <div className="text-center py-4"><Spinner animation="border" size="sm" /></div> : (
            <Table responsive className="mb-0">
              <thead><tr><th>Name</th><th>Added</th><th>Status</th><th>Businesses referred</th><th>Their link</th></tr></thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="5" className="text-center text-muted py-3">None yet.</td></tr>
                ) : rows.map((a) => (
                  <tr key={a.id}>
                    <td>{a.name}{!a.isActive && <Badge bg="secondary" className="ms-2">Inactive</Badge>}<div className="small text-muted">{a.email}</div></td>
                    <td>{formatDate(a.addedAt)}</td>
                    <td>{a.signedIn ? <Badge bg="success">Signed in</Badge> : <Badge bg="secondary">Invited</Badge>}</td>
                    <td>{a.referred}</td>
                    <td><code>{a.referralCode}</code> <Button variant="link" size="sm" className="p-0 ms-1" onClick={() => copy(a.link)}>Copy link</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      {rows && rows.length > 0 && <Alert variant="light" className="mt-3 small mb-0">You see who you added and how many businesses they referred, not what they earn.</Alert>}
    </div>
  );
}

export default AccountManagerAffiliates;
